/**
 * @file LeaderboardPage ("/leaderboard") — current competition's leaderboard.
 *
 * The leaderboard is scoped to the current competition, so we resolve the
 * current season/week from CompetitionService first, then load the leaderboard
 * (mock data today, live backend later). Split into an inner component to keep
 * hook order stable while season/week resolve.
 */

import { useCurrentCompetition, useLeaderboard } from '../hooks/data';
import { LeaderboardTable } from '../components/LeaderboardTable';
import { LoadingState, ErrorState, EmptyState } from '../components/StateViews';
import { seasonWeekLabel } from '../utils/format';

/** @param {{ season: number, week: number }} props */
function LeaderboardForCompetition({ season, week }) {
  const { data, loading, error, reload } = useLeaderboard(season, week);

  if (loading) return <LoadingState label="Loading leaderboard…" />;
  if (error || !data) {
    return (
      <ErrorState error={error} onRetry={reload} label="Couldn't load the leaderboard." />
    );
  }
  if (data.entries.length === 0) {
    return <EmptyState label="No times posted yet. Be the first to race!" />;
  }
  return <LeaderboardTable leaderboard={data} />;
}

export function LeaderboardPage() {
  const { data: competition, loading, error, reload } = useCurrentCompetition();

  return (
    <div className="scrw-leaderboard-page">
      <div className="scrw-page-head">
        <h1 className="scrw-page-head__title">Leaderboard</h1>
        {competition ? (
          <p className="scrw-page-head__sub">
            {seasonWeekLabel(competition.season, competition.week)} · {competition.title}
          </p>
        ) : null}
      </div>

      {loading ? <LoadingState label="Loading current competition…" /> : null}

      {!loading && (error || !competition) ? (
        <ErrorState
          error={error}
          onRetry={reload}
          label="Couldn't determine the current competition."
        />
      ) : null}

      {!loading && competition ? (
        <LeaderboardForCompetition
          season={competition.season}
          week={competition.week}
        />
      ) : null}
    </div>
  );
}
